import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DownloadIcon } from "lucide-react";
import {
  FieldErrors,
  UseFormSetError,
  UseFormClearErrors,
} from "react-hook-form";
import { FileUploadZone } from "./components/file-upload-zone";
import { StepCard } from "./components/step-card";

interface CsvUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  onFinalSubmit?: () => Promise<void>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  errors: FieldErrors<any>;
  maxFileSize?: number;
  templateUrl?: string;
}

type UploadStatus = "idle" | "uploading" | "success" | "error";

export interface FileUploadState {
  file: File | null;
  status: UploadStatus;
  errorMessage?: string;
}

const DEFAULT_MAX_FILE_SIZE = 10;
const DEFAULT_TEMPLATE_URL = "/example.csv";

const validateFile = (file: File, maxSizeMB: number): string | null => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxSizeBytes) {
    return `Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`;
  }

  if (!file.name.toLowerCase().endsWith(".csv")) {
    return "Apenas arquivos CSV são aceitos";
  }

  return null;
};

export const CsvUploadModal: React.FC<CsvUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  onFinalSubmit,
  setError,
  clearErrors,
  errors,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  templateUrl = DEFAULT_TEMPLATE_URL,
}) => {
  const [uploadState, setUploadState] = useState<FileUploadState>({
    file: null,
    status: "idle",
  });

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file, maxFileSize);

      if (validationError) {
        setUploadState({
          file,
          status: "error",
          errorMessage: validationError,
        });
        setError("csvFile", {
          type: "manual",
          message: validationError,
        });
        return;
      }

      setUploadState({
        file,
        status: "idle",
      });
      clearErrors("csvFile");
    },
    [maxFileSize, setError, clearErrors],
  );

  const handleFileRemove = useCallback(() => {
    setUploadState({
      file: null,
      status: "idle",
    });
    clearErrors("csvFile");
  }, [clearErrors]);

  const handleUpload = useCallback(async () => {
    if (!uploadState.file) return;

    setUploadState((prev) => ({ ...prev, status: "uploading" }));
    clearErrors("csvFile");

    try {
      await onUpload(uploadState.file);
      resetAndClose();
    } catch (err: any) {
      const errorMessage =
        err?.message || "Erro ao enviar arquivo. Tente novamente.";
      setUploadState((prev) => ({
        ...prev,
        status: "error",
        errorMessage,
      }));
      setError("csvFile", {
        type: "manual",
        message: errorMessage,
      });
    }
  }, [uploadState.file, onUpload, clearErrors, setError]);

  const handleFinalSubmit = useCallback(async () => {
    try {
      onFinalSubmit && (await onFinalSubmit());
      resetAndClose();
    } catch (error) {
      console.error("Error in final submit:", error);
    }
  }, [onFinalSubmit]);

  const handleDownloadTemplate = useCallback(() => {
    window.open(templateUrl, "_blank");
  }, [templateUrl]);

  const resetAndClose = useCallback(() => {
    setUploadState({
      file: null,
      status: "idle",
    });
    clearErrors("csvFile");
    onClose();
  }, [clearErrors, onClose]);

  const isUploading = uploadState.status === "uploading";
  const isSuccess = uploadState.status === "success";
  const hasFile = !!uploadState.file;

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Importar planilha</DialogTitle>
          <p className="text-muted-foreground text-sm mt-1">
            Use o modelo abaixo para organizar seus dados com facilidade.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <StepCard step={1} title="Baixe o modelo">
            <div className="text-sm text-gray-600 mb-3">
              Use o modelo abaixo como referência para preencher sua planilha.
              Ele já vem com todas as colunas e a ordem corretas.
            </div>
            <Button
              variant="default"
              className="bg-black hover:bg-zinc-900 w-full"
              onClick={handleDownloadTemplate}
            >
              <DownloadIcon className="w-4 h-4 mr-2" />
              Baixar modelo
            </Button>
          </StepCard>

          <StepCard step={2} title="Envie a planilha preenchida">
            <div className="text-sm text-gray-600 mb-4">
              Arraste o arquivo aqui ou clique para selecionar do seu
              computador.
            </div>

            <FileUploadZone
              state={uploadState}
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              maxFileSize={maxFileSize}
              isUploading={isUploading}
              errorMessage={errors.csvFile?.message as string}
            />
          </StepCard>
        </div>

        <DialogFooter className="gap-2">
          {!isSuccess && (
            <Button
              onClick={handleUpload}
              disabled={!hasFile || isUploading}
              className="min-w-[100px]"
            >
              {isUploading ? "Enviando..." : "Enviar"}
            </Button>
          )}

          {isSuccess && (
            <Button
              onClick={handleFinalSubmit}
              variant="default"
              className="min-w-[120px]"
            >
              Confirmar envio
            </Button>
          )}

          <Button
            variant="outline"
            onClick={resetAndClose}
            disabled={isUploading}
          >
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
